<template>
  <div class="org-tree-panel" v-loading="loading">
    <div class="org-tree-panel__search">
      <el-input
        v-model="filterText"
        placeholder="Поиск по названию или коду отхода"
        prefix-icon="el-icon-search"
        clearable
        size="small"
      >
      </el-input>
      <help-tip title="Цвет кода отхода" placement="bottom-start">
        <div class="danger-legend">
          <div v-for="item in DANGER_LEGEND" :key="item.cls" class="danger-legend__item">
            <span class="waste__code danger-legend__code" :class="item.cls">
              {{ item.label }}
            </span>
          </div>
        </div>
      </help-tip>
    </div>
    <!-- Отступ уровней и линии вложенности рисуются в самой строке, отступ дерева - нулевой -->
    <vue-easy-tree
      ref="tree"
      class="org-tree"
      height="100%"
      :item-size="ITEM_SIZE"
      :indent="0"
      :data="tree"
      node-key="key"
      :props="defaultProps"
      :filter-node-method="filterNode"
      empty-text="Ничего не найдено"
      expand-on-click-node
      highlight-current
      @current-change="onSelect"
      @node-expand="saveExpanded"
      @node-collapse="saveExpanded"
    >
      <template #default="{ node, data }">
        <div
          class="tree-row"
          :style="{
            paddingLeft: (node.level - 1) * INDENT + 'px',
            backgroundSize: (node.level - 1) * INDENT + 'px 100%',
          }"
        >
          <div
            v-if="data.isWaste"
            class="waste"
            :class="[dangerClass(data.dangerDegree), { 'is-current': node.isCurrent }]"
            :title="wasteTitle(data)"
          >
            <span class="waste__code">{{ data.code }}</span>
            <span class="waste__name">{{ data.wasteName }}</span>
          </div>
          <div
            v-else
            class="unit"
            :class="{ 'is-open': node.expanded && node.childNodes.length }"
            :title="data.name"
          >
            <i
              class="unit__arrow el-icon-arrow-right"
              :class="{ 'is-expanded': node.expanded }"
            ></i>
            <span class="unit__name">{{ data.name }}</span>
          </div>
        </div>
      </template>
    </vue-easy-tree>
  </div>
</template>

<script>
import VueEasyTree from "@wchbrad/vue-easy-tree/index.js";
import HelpTip from "@/components/ecologist/dataEntryNew/helpTip";

// Раскрытые узлы дерева запоминаются между заходами на страницу
const EXPANDED_KEY = "dataEntryNew.treeExpanded";
// Высота строки: блок 28px и промежуток 4px над ним
const ITEM_SIZE = 32;
// Сдвиг уровня вложенности, линия уровня - у левого края сдвига
const INDENT = 12;
// Легенда цветов плашки кода - классы те же, что выдаёт dangerClass
const DANGER_LEGEND = [
  { cls: "is-class-1", label: "Первый класс" },
  { cls: "is-class-2", label: "Второй класс" },
  { cls: "is-class-3", label: "Третий класс" },
  { cls: "is-class-4", label: "Четвертый класс" },
  { cls: "is-safe", label: "Неопасные" },
  { cls: "is-unknown", label: "Класс не определен" },
];

export default {
  name: "orgTreePanel",
  components: { VueEasyTree, HelpTip },
  data() {
    return {
      ITEM_SIZE,
      INDENT,
      DANGER_LEGEND,
      loading: false,
      filterText: "",
      // Выделенный отход: клик по подразделению только раскрывает его, выделение остаётся на отходе
      currentKey: null,
      expandedBeforeFilter: null,
      tree: [],
      defaultProps: {
        children: "children",
        label: "name",
      },
    };
  },

  watch: {
    filterText(val, oldVal) {
      if (val && !oldVal) this.expandedBeforeFilter = this.expandedKeys();
      this.$refs.tree.filter(val);
      if (!val && this.expandedBeforeFilter) {
        this.restoreExpanded(this.expandedBeforeFilter);
        this.expandedBeforeFilter = null;
      }
    },
  },

  methods: {
    expandedKeys() {
      const nodesMap = this.$refs.tree.store.nodesMap;
      return new Set(
        Object.keys(nodesMap).filter((key) => nodesMap[key].expanded)
      );
    },

    restoreExpanded(keys) {
      const nodesMap = this.$refs.tree.store.nodesMap;
      Object.keys(nodesMap).forEach((key) => {
        if (keys.has(key)) nodesMap[key].expand();
        else nodesMap[key].collapse();
      });
    },

    // Раскрытие при поиске не запоминаем: после очистки поиска дерево и так возвращается как было.
    // Событие сворачивания приходит до сворачивания узла - сохраняем на следующем тике
    saveExpanded() {
      this.$nextTick(() => {
        if (this.filterText) return;
        try {
          localStorage.setItem(EXPANDED_KEY, JSON.stringify([...this.expandedKeys()]));
        } catch (e) {
          // раскрытие просто не запомнится
        }
      });
    },

    restoreSaved() {
      let keys = null;
      try {
        keys = JSON.parse(localStorage.getItem(EXPANDED_KEY));
      } catch (e) {
        // хранилище недоступно или испорчено - дерево остаётся свёрнутым
      }
      if (Array.isArray(keys)) this.restoreExpanded(new Set(keys));
    },

    // Узел виден, если совпал он сам или любое подразделение над ним:
    // найденное подразделение показывается со всеми своими отходами
    filterNode(value, data, node) {
      if (!value) return true;
      const query = value.toLowerCase();
      for (let current = node; current && current.level > 0; current = current.parent)
        if (current.data.name.toLowerCase().includes(query)) return true;
      return false;
    },

    onSelect(data) {
      if (!data.isWaste) {
        this.setCurrent(this.currentKey);
        return;
      }
      this.currentKey = data.key;
      this.$emit("select", data, this.unitPath(this.$refs.tree.getNode(data.key)));
    },

    unitPath(node) {
      const names = [];
      for (let parent = node && node.parent; parent && parent.level > 0; parent = parent.parent)
        names.unshift(parent.data.name);
      return names;
    },

    // Выбрать отход по ключу как кликом: раскрыть подразделения над ним и прокрутить к нему.
    // false - такого отхода в дереве больше нет
    selectByKey(key) {
      const tree = this.$refs.tree;
      const node = key && tree.getNode(key);
      if (!node || !node.data.isWaste) return false;
      for (let parent = node.parent; parent && parent.level > 0; parent = parent.parent)
        parent.expand();
      this.saveExpanded();
      this.setCurrent(key);
      this.$emit("select", node.data, this.unitPath(node));
      this.$nextTick(() => tree.scrollToItem(key));
      return true;
    },

    // Программно выделить узел (без события select), null - снять выделение
    setCurrent(key) {
      this.currentKey = key;
      this.$refs.tree.setCurrentKey(key);
    },

    // Цвет плашки кода - по классу опасности. В справочнике «первый класс» ... «четвертый класс»,
    // «неопасные», «не определен»; цифры и римские номера - на случай другого написания
    dangerClass(degree) {
      const text = (degree || "").toLowerCase();
      if (text.includes("неопас")) return "is-safe";
      const digit = /[1-4]/.exec(text);
      if (digit) return "is-class-" + digit[0];
      const words = ["перв", "втор", "трет", "четв"];
      const word = words.findIndex((w) => text.includes(w));
      if (word >= 0) return "is-class-" + (word + 1);
      const roman = /\b(iv|i{1,3})\b/.exec(text);
      if (roman) return "is-class-" + (["i", "ii", "iii", "iv"].indexOf(roman[1]) + 1);
      return "is-unknown";
    },

    // Название в строке обрезается - полностью оно, класс и тех. процесс в подсказке
    wasteTitle(data) {
      return [
        `${data.code} ${data.wasteName}`,
        data.dangerDegree,
        data.processName && `Тех. процесс: ${data.processName}`,
      ]
        .filter(Boolean)
        .join("\n");
    },
  },

  async mounted() {
    this.loading = true;
    try {
      this.tree = await this.$store.dispatch("getDataEntryTree");
    } finally {
      this.loading = false;
    }
    await this.$nextTick();
    this.restoreSaved();
    this.$emit("loaded");
  },
};
</script>

<style scoped>
/* Блок в рамке, как календарь и таблица справа */
.org-tree-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  box-sizing: border-box;
  border: 1.5px solid #dcdfe6;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
}
.org-tree-panel__search {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border-bottom: 1px solid #ebeef5;
  background: #fafbfc;
}
.org-tree {
  flex: 1;
  min-height: 0;
  background: transparent;
  /* Клики раскрывают узлы - текст при этом не выделяется */
  user-select: none;
}
/* Отступы - внутри прокручиваемой области: полоса прокрутки остаётся у рамки */
.org-tree ::v-deep .vue-recycle-scroller {
  box-sizing: border-box;
  padding: 8px 12px 12px;
}
/* Стрелку, подсветку наведения и выделения строки рисуют сами блоки строки */
.org-tree ::v-deep .el-tree-node__expand-icon {
  display: none;
}
.org-tree ::v-deep .el-tree-node__content {
  background-color: transparent !important;
}

/*
  Строка: промежуток 4px сверху и блок на остальную высоту.
  Слева - отступ уровня с вертикальными линиями всех уровней выше (шаг - INDENT):
  линия продолжает левую границу раскрытого подразделения вниз, через все его отходы
*/
.tree-row {
  flex: 1;
  min-width: 0;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  padding-top: 4px;
  background-image: repeating-linear-gradient(
    to right,
    #dcdfe6 0 2px,
    transparent 2px 12px
  );
  background-repeat: no-repeat;
}

/* Подразделение */
.unit {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 10px 0 6px;
  border: 1px solid #ebeef5;
  border-left: 2px solid #dcdfe6;
  border-radius: 6px;
  background: #fafbfc;
  color: #303133;
  transition: background-color 0.15s, border-color 0.15s;
}
/* Раскрытое: левая граница переходит в линию уровня под ним */
.unit.is-open {
  border-bottom-left-radius: 0;
}
.unit:hover {
  border-color: #b3d8ff;
  border-left-color: #dcdfe6;
  background: #ecf5ff;
}
.unit__arrow {
  font-size: 12px;
  color: #909399;
  transition: transform 0.2s;
}
.unit__arrow.is-expanded {
  transform: rotate(90deg);
}
.unit__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 500;
}

/* Отход: плашка кода отхода цвета класса опасности и название отдельными блоками */
.waste {
  flex: 1;
  min-width: 0;
  display: flex;
}
/* Цвета - от класса опасности на самой плашке или на строке отхода, серый - класс не определен */
.waste__code {
  flex-shrink: 0;
  min-width: 62px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 7px;
  border: 1px solid var(--code-border, #dcdfe6);
  border-radius: 6px 0 0 6px;
  background: var(--code-bg, #f4f4f5);
  color: var(--code-text, #909399);
  font-size: 11px;
  font-variant-numeric: tabular-nums;
}
.waste__name {
  flex: 1;
  min-width: 0;
  padding: 0 10px 0 9px;
  border: 1px solid #ebeef5;
  border-left: none;
  border-radius: 0 6px 6px 0;
  background: #fff;
  color: #606266;
  font-size: 12px;
  line-height: 26px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: background-color 0.15s, border-color 0.15s;
}
.waste:hover .waste__name {
  border-color: #b3d8ff;
  background: #ecf5ff;
}
/* Выбранный отход - синяя рамка 2px вокруг кода и названия (тенью, чтобы строка не сдвигалась) */
.waste.is-current .waste__code,
.waste.is-current .waste__name {
  border-color: #409eff;
}
.waste.is-current .waste__code {
  box-shadow: inset 0 0 0 1px #409eff;
}
.waste.is-current .waste__name {
  box-shadow: inset 0 1px #409eff, inset -1px 0 #409eff, inset 0 -1px #409eff;
  color: #303133;
}
.is-class-1 {
  --code-bg: #fee2e2;
  --code-border: #fca5a5;
  --code-text: #991b1b;
}
.is-class-2 {
  --code-bg: #fed7aa;
  --code-border: #fb923c;
  --code-text: #7c2d12;
}
.is-class-3 {
  --code-bg: #fef9c3;
  --code-border: #fde047;
  --code-text: #854d0e;
}
.is-class-4 {
  --code-bg: #dcfce7;
  --code-border: #86efac;
  --code-text: #166534;
}
.is-safe {
  --code-bg: #ccfbf1;
  --code-border: #5eead4;
  --code-text: #115e59;
}

/* Легенда в подсказке: плашки тех же цветов, по одной на класс */
.danger-legend {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}
.danger-legend__code {
  border-radius: 6px;
  line-height: 20px;
  font-size: 12px;
}
</style>
